/**
 * Device profiles (decisions 9, 11): the page the preview paginates against. The PDF profile is the exported
 * page itself; the e-reader, phone and tablet profiles stand in for a screen that reflows the EPUB with its
 * own fonts and line breaking, so they carry the "approximate" flag. Part 05 log, table P. Lengths are in
 * PostScript points (1/72 in), the layout engine's and the PDF's unit.
 */
import { frozen } from './record.ts';
import { THEME_FIELDS } from './theme.ts';

/** A device profile's ID. */
export type ProfileId = 'generic-6in' | 'kindle' | 'phone' | 'tablet' | 'pdf-a5';

/** Space between the page edge and the text, in points. On a facing page `inner` is the spine side. */
export interface Margins {
  readonly top: number;
  readonly bottom: number;
  readonly inner: number;
  readonly outer: number;
}

/** A page target. */
export interface DeviceProfile {
  readonly id: ProfileId;
  /** Page width and height, points. */
  readonly width: number;
  readonly height: number;
  readonly margins: Margins;
  /** True when the reading device reflows the book itself, so the preview is a close stand-in (decision 9). */
  readonly approximate: boolean;
  /** True when pages face each other (print), so `inner` and `outer` swap sides on left-hand pages. */
  readonly facing: boolean;
}

/** Points in `n` millimetres. */
export const mm = (n: number): number => (n * 72) / 25.4;
/** Points in `n` inches. */
export const inches = (n: number): number => n * 72;
/** Points in `n` CSS pixels (CSS fixes 96 px to the inch). */
export const cssPx = (n: number): number => (n * 72) / 96;

const even = (side: number): Margins => ({ top: side, bottom: side, inner: side, outer: side });

/** The generic 6-inch e-reader, a 3:4 screen of 3.6 × 4.8 in: the profile a writer starts with (decision 11). */
export const DEFAULT_PROFILE: DeviceProfile = frozen({
  id: 'generic-6in',
  width: inches(3.6),
  height: inches(4.8),
  margins: even(18),
  approximate: true,
  facing: false,
});

/** Every profile, in the order the picker lists them. */
export const DEVICE_PROFILES: readonly DeviceProfile[] = frozen([
  DEFAULT_PROFILE,
  // Kindle Paperwhite (6.8 in): 1236 × 1648 pixels at 300 ppi.
  { id: 'kindle', width: inches(1236 / 300), height: inches(1648 / 300), margins: even(20), approximate: true, facing: false },
  // A 6.1-inch phone held upright: a 390 × 844 CSS-pixel viewport.
  { id: 'phone', width: cssPx(390), height: cssPx(844), margins: even(16), approximate: true, facing: false },
  // A 10.9-inch tablet held upright: an 820 × 1180 CSS-pixel viewport.
  { id: 'tablet', width: cssPx(820), height: cssPx(1180), margins: even(48), approximate: true, facing: false },
  // An A5 book page, printed facing: the PDF export's page (decision 8).
  {
    id: 'pdf-a5',
    width: mm(148),
    height: mm(210),
    margins: { top: mm(18), bottom: mm(20), inner: mm(20), outer: mm(15) },
    approximate: false,
    facing: true,
  },
]);

/**
 * The profile with `id`, or {@link DEFAULT_PROFILE} for any other value: a remembered choice may name a
 * profile a later build no longer has, and the preview must still open.
 */
export function profileById(id: unknown): DeviceProfile {
  return DEVICE_PROFILES.find((profile) => profile.id === id) ?? DEFAULT_PROFILE;
}

/** The text area's width and height, points. */
export function textArea(profile: DeviceProfile): { readonly width: number; readonly height: number } {
  const { top, bottom, inner, outer } = profile.margins;
  return { width: profile.width - inner - outer, height: profile.height - top - bottom };
}

/** Ems of the largest body size every profile's text line holds at least (P6): the largest first-line indent and 8 ems more. */
export const MIN_MEASURE_EM = 12;
/** Lines of the largest body size at the largest line height every profile's page holds at least (P6). */
export const MIN_PAGE_LINES = 8;

/**
 * Whether `profile` has room for a body paragraph under any theme (P6): a line of {@link MIN_MEASURE_EM} ems
 * and a page of {@link MIN_PAGE_LINES} lines at the largest body size and line height a theme allows. Every
 * profile in {@link DEVICE_PROFILES} does. It says nothing about nested blocks: a quote takes its indent off
 * both sides at every level and quotes nest without a theme limit, so no page width can hold every depth;
 * the layout engine clamps nested indents to keep a measure (part 14).
 */
export function fitsEveryTheme(profile: DeviceProfile): boolean {
  const { width, height } = textArea(profile);
  const largest = THEME_FIELDS.bodySize.max / 2;
  const line = (largest * THEME_FIELDS.lineHeight.max) / 100;
  return width >= MIN_MEASURE_EM * largest && height >= MIN_PAGE_LINES * line;
}
